/* eslint-disable react/prop-types */
import { useState } from "react";
import { Form } from "react-bootstrap";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FaGripVertical } from "react-icons/fa";

const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: "0.35" } },
  }),
};

function SortableLanguageRow({
  code,
  label,
  flagSrc,
  order,
  onTurnOff,
  dragHandleAriaLabel,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: code });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`client-lang-sortable-row${isDragging ? " client-lang-sortable-row--dragging" : ""}`}
    >
      <button
        type="button"
        className="client-lang-drag-handle"
        aria-label={dragHandleAriaLabel}
        {...attributes}
        {...listeners}
      >
        <FaGripVertical aria-hidden />
      </button>
      <span className="client-lang-label-with-flag">
        {typeof order === "number" ? (
          <span className="client-lang-order" aria-hidden>
            {order}
          </span>
        ) : null}
        {flagSrc ? (
          <img
            className="client-lang-flag"
            src={flagSrc}
            alt=""
            width={24}
            height={18}
          />
        ) : null}
        <span className="client-lang-sortable-label text-truncate">{label}</span>
      </span>
      <Form.Check
        type="switch"
        id={`lang-enabled-${code}`}
        checked
        className="client-lang-sortable-switch ms-2 mb-0"
        onChange={() => onTurnOff(code)}
      />
    </div>
  );
}

/**
 * Vertical drag-and-drop list for enabled client languages (order = customer app).
 */
export default function ClientLanguagesSortableList({
  items,
  getLabel,
  getFlag,
  onReorder,
  onTurnOff,
  dragHandleAriaLabel,
}) {
  const [activeId, setActiveId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = items.indexOf(active.id);
    const newIndex = items.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const activeCode = activeId != null ? String(activeId) : null;
  const overlayLabel =
    activeCode && getLabel ? getLabel(activeCode) : "";
  const overlayFlag = activeCode && getFlag ? getFlag(activeCode) : undefined;
  const overlayOrder = activeCode ? items.indexOf(activeCode) + 1 : 0;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <div className="client-lang-sortable-list">
          {items.map((code, index) => (
            <SortableLanguageRow
              key={code}
              code={code}
              order={index + 1}
              label={getLabel(code)}
              flagSrc={getFlag ? getFlag(code) : undefined}
              onTurnOff={onTurnOff}
              dragHandleAriaLabel={dragHandleAriaLabel}
            />
          ))}
        </div>
      </SortableContext>
      <DragOverlay dropAnimation={dropAnimation}>
        {activeCode ? (
          <div className="client-lang-sortable-row client-lang-sortable-row--overlay">
            <span className="client-lang-drag-handle client-lang-drag-handle--static">
              <FaGripVertical aria-hidden />
            </span>
            <span className="client-lang-label-with-flag">
              {overlayOrder > 0 ? (
                <span className="client-lang-order" aria-hidden>
                  {overlayOrder}
                </span>
              ) : null}
              {overlayFlag ? (
                <img
                  className="client-lang-flag"
                  src={overlayFlag}
                  alt=""
                  width={24}
                  height={18}
                />
              ) : null}
              <span className="client-lang-sortable-label text-truncate">
                {overlayLabel}
              </span>
            </span>
            <span className="client-lang-sortable-switch-placeholder" />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
